import { Colors } from '@back-office/core';
import {
  GcpBox,
  GcpPasswordInput,
  GcpPublicWrapper,
  GcpTypography,
  Submerchant,
} from '@back-office/ui';
import { Button } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import makeStyles from '@material-ui/core/styles/makeStyles';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import { observer } from 'mobx-react';
import React, { FC } from 'react';
import { Controller, UseFormMethods } from 'react-hook-form';
import { FieldErrors } from 'react-hook-form/dist/types/errors';
import { Control } from 'react-hook-form/dist/types/form';
import { useTranslation } from 'react-i18next';
import { InvitationFieldNames, InvitationForm } from '../../forms';

const useStyles = makeStyles(
  () => ({
    span: {
      color: 'initial',
    },
  }),
  { name: 'Register' }
);

interface RegisterProps {
  email: string;
  handleAtLeast: (value: string) => keyof Colors;
  handleLowerCase: (value: string) => keyof Colors;
  handleUpperCase: (value: string) => keyof Colors;
  handleContainNumber: (value: string) => keyof Colors;
  handleSpecialCharacter: (value: string) => keyof Colors;
  watchPassword: string;
  handleSubmit: UseFormMethods['handleSubmit'];
  onSubmit: (data: InvitationForm) => Promise<void>;
  watchConfirmPassword: string;
  isValid: boolean;
  errors: FieldErrors<InvitationForm>;
  control: Control<InvitationForm>;
}

const Register: FC<RegisterProps> = observer((props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const {
    email,
    handleLowerCase,
    handleAtLeast,
    handleUpperCase,
    handleContainNumber,
    handleSpecialCharacter,
    watchPassword,
    handleSubmit,
    onSubmit,
    watchConfirmPassword,
    isValid,
    errors,
    control,
  } = props;

  return (
    <GcpPublicWrapper noLogo>
      <Grid container>
        <Grid item lg={6} xs={12}>
          <GcpBox
            isFullWidth
            alignItems="center"
            display="flex"
            flexDirection="column"
            justifyContent="center"
          >
            <GcpBox>
              <GcpTypography lineHeight={0.5} size={196}>
                <Submerchant />
              </GcpTypography>
            </GcpBox>
          </GcpBox>
        </Grid>
        <Grid item lg={6} xs={12}>
          <GcpBox
            isFullWidth
            alignItems="flex-start"
            display="flex"
            flexDirection="column"
            justifyContent="center"
          >
            <GcpBox>
              <GcpTypography
                paint="tundora"
                size={36}
                variant="h1"
                weight="bold"
              >
                {t('invitations.create_new_password')}
              </GcpTypography>
            </GcpBox>
            <GcpBox mt={3}>
              <GcpTypography paint="hitGray" variant="body2">
                {t('invitations.user', { email: email || '' })}
              </GcpTypography>
            </GcpBox>
            <GcpBox mt={2}>
              <GcpTypography
                paint={handleAtLeast(watchPassword)}
                variant="body2"
              >
                <CheckCircleOutlineIcon />
                <span className={classes.span}>
                  &nbsp;{t('invitations.at_least_8_characters')}
                </span>
              </GcpTypography>
              <GcpTypography
                paint={handleLowerCase(watchPassword)}
                variant="body2"
              >
                <CheckCircleOutlineIcon />
                <span className={classes.span}>
                  &nbsp;{t('invitations.lower_case')}
                </span>
              </GcpTypography>
              <GcpTypography
                paint={handleUpperCase(watchPassword)}
                variant="body2"
              >
                <CheckCircleOutlineIcon />
                <span className={classes.span}>
                  &nbsp;{t('invitations.upper_case')}
                </span>
              </GcpTypography>
              <GcpTypography
                paint={handleContainNumber(watchPassword)}
                variant="body2"
              >
                <CheckCircleOutlineIcon />
                <span className={classes.span}>
                  &nbsp;{t('invitations.contains_numbers')}
                </span>
              </GcpTypography>
              <GcpTypography
                paint={handleSpecialCharacter(watchPassword)}
                variant="body2"
              >
                <CheckCircleOutlineIcon />
                <span className={classes.span}>
                  &nbsp;{t('invitations.special_characters')}
                </span>
              </GcpTypography>
            </GcpBox>
            <GcpBox isFullWidth mt={5}>
              <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
                <Controller
                  control={control}
                  defaultValue=""
                  name={InvitationFieldNames.Password}
                  render={({ onChange, name, value, onBlur }) => (
                    <GcpPasswordInput
                      label={t('invitations.new_password')}
                      name={name}
                      value={value}
                      onBlur={onBlur}
                      onChange={onChange}
                    />
                  )}
                  rules={{
                    required: true,
                  }}
                />
                <GcpBox isFullWidth mt={2}>
                  <Controller
                    control={control}
                    defaultValue=""
                    name={InvitationFieldNames.ConfirmPassword}
                    render={({ onChange, name, value, onBlur }) => (
                      <GcpPasswordInput
                        label={t('invitations.confirm_password')}
                        name={name}
                        value={value}
                        onBlur={onBlur}
                        onChange={onChange}
                      />
                    )}
                    rules={{
                      required: true,
                      validate: (value) => value === watchPassword,
                    }}
                  />
                </GcpBox>
                {errors.confirmPassword?.type === 'validate' && (
                  <GcpBox mt={2}>
                    <GcpTypography paint="flamingo" variant="body1">
                      {t('error.not_match')}
                    </GcpTypography>
                  </GcpBox>
                )}
                <GcpBox mt={2}>
                  <Button
                    color="primary"
                    disabled={!watchConfirmPassword || !isValid}
                    size="large"
                    type="submit"
                    variant="contained"
                  >
                    {t('dialog.create')}
                  </Button>
                </GcpBox>
              </form>
            </GcpBox>
          </GcpBox>
        </Grid>
      </Grid>
    </GcpPublicWrapper>
  );
});

export default Register;
